/**
 * Skeleton Loading Component
 *
 * Provides skeleton loading states for various UI elements.
 * Features:
 * - Multiple predefined skeleton types (text, avatar, card, list, table)
 * - Customizable dimensions
 * - Animated pulse effect
 * - Accessible with aria attributes
 */

/**
 * Skeleton variant types
 */
export type SkeletonVariant = 'text' | 'circular' | 'rectangular' | 'rounded';

/**
 * Skeleton options
 */
export interface SkeletonOptions {
  /** Width (CSS value or number for pixels) */
  width?: string | number;
  /** Height (CSS value or number for pixels) */
  height?: string | number;
  /** Skeleton shape variant */
  variant?: SkeletonVariant;
  /** Additional CSS classes */
  className?: string;
  /** Whether to animate (default: true) */
  animate?: boolean;
}

/**
 * List skeleton options
 */
export interface ListSkeletonOptions {
  /** Number of items to render */
  items?: number;
  /** Show avatar placeholder */
  showAvatar?: boolean;
  /** Number of text lines per item */
  lines?: number;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Card skeleton options
 */
export interface CardSkeletonOptions {
  /** Show header area */
  showHeader?: boolean;
  /** Show image placeholder */
  showImage?: boolean;
  /** Number of content text lines */
  lines?: number;
  /** Show action buttons area */
  showActions?: boolean;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Table skeleton options
 */
export interface TableSkeletonOptions {
  /** Number of rows */
  rows?: number;
  /** Number of columns */
  columns?: number;
  /** Show header row */
  showHeader?: boolean;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Create a basic skeleton element
 */
export function createSkeleton(options: SkeletonOptions = {}): HTMLElement {
  const {
    width,
    height,
    variant = 'text',
    className = '',
    animate = true,
  } = options;

  const element = document.createElement('div');
  element.className = `skeleton bg-muted ${animate ? 'animate-pulse' : ''} ${className}`.trim();
  element.setAttribute('role', 'status');
  element.setAttribute('aria-label', 'Loading...');

  // Apply variant-specific styles
  switch (variant) {
    case 'circular':
      element.classList.add('rounded-full');
      break;
    case 'rounded':
      element.classList.add('rounded-lg');
      break;
    case 'rectangular':
      element.classList.add('rounded-none');
      break;
    case 'text':
    default:
      element.classList.add('rounded');
      break;
  }

  // Apply dimensions
  if (width !== undefined) {
    element.style.width = typeof width === 'number' ? `${width}px` : width;
  }
  if (height !== undefined) {
    element.style.height = typeof height === 'number' ? `${height}px` : height;
  } else if (variant === 'text') {
    element.style.height = '1rem';
  }

  // Add screen reader text
  const srOnly = document.createElement('span');
  srOnly.className = 'sr-only';
  srOnly.textContent = 'Loading...';
  element.appendChild(srOnly);

  return element;
}

/**
 * Create a text line skeleton
 */
export function createTextSkeleton(width: string | number = '100%'): HTMLElement {
  return createSkeleton({
    variant: 'text',
    width,
    height: '1rem',
  });
}

/**
 * Create an avatar skeleton
 */
export function createAvatarSkeleton(size: number = 40): HTMLElement {
  return createSkeleton({
    variant: 'circular',
    width: size,
    height: size,
  });
}

/**
 * Create a list item skeleton
 */
export function createListItemSkeleton(options: { showAvatar?: boolean; lines?: number } = {}): HTMLElement {
  const { showAvatar = true, lines = 2 } = options;

  const item = document.createElement('div');
  item.className = 'flex items-start gap-3 p-3';
  item.setAttribute('role', 'status');
  item.setAttribute('aria-label', 'Loading list item...');

  if (showAvatar) {
    item.appendChild(createAvatarSkeleton(40));
  }

  const content = document.createElement('div');
  content.className = 'flex-1 space-y-2';

  for (let i = 0; i < lines; i++) {
    const width = i === 0 ? '75%' : i === lines - 1 ? '50%' : '90%';
    content.appendChild(createTextSkeleton(width));
  }

  item.appendChild(content);

  return item;
}

/**
 * Create a list skeleton with multiple items
 */
export function createListSkeleton(options: ListSkeletonOptions = {}): HTMLElement {
  const {
    items = 3,
    showAvatar = true,
    lines = 2,
    className = '',
  } = options;

  const container = document.createElement('div');
  container.className = `skeleton-list space-y-1 ${className}`.trim();
  container.setAttribute('role', 'status');
  container.setAttribute('aria-label', 'Loading list...');

  for (let i = 0; i < items; i++) {
    const item = createListItemSkeleton({ showAvatar, lines });
    if (i < items - 1) {
      item.classList.add('border-b', 'border-border/50');
    }
    container.appendChild(item);
  }

  return container;
}

/**
 * Create a card skeleton
 */
export function createCardSkeleton(options: CardSkeletonOptions = {}): HTMLElement {
  const {
    showHeader = true,
    showImage = false,
    lines = 3,
    showActions = false,
    className = '',
  } = options;

  const card = document.createElement('div');
  card.className = `skeleton-card glass-sm p-4 rounded-xl ${className}`.trim();
  card.setAttribute('role', 'status');
  card.setAttribute('aria-label', 'Loading card...');

  // Image area
  if (showImage) {
    const image = createSkeleton({
      variant: 'rounded',
      width: '100%',
      height: 160,
      className: 'mb-4',
    });
    card.appendChild(image);
  }

  // Header area
  if (showHeader) {
    const header = document.createElement('div');
    header.className = 'flex items-center gap-3 mb-4';

    const avatar = createAvatarSkeleton(32);
    header.appendChild(avatar);

    const headerText = document.createElement('div');
    headerText.className = 'flex-1 space-y-1';
    headerText.appendChild(createTextSkeleton('60%'));
    headerText.appendChild(createSkeleton({ width: '40%', height: 12 }));
    header.appendChild(headerText);

    card.appendChild(header);
  }

  // Content lines
  const content = document.createElement('div');
  content.className = 'space-y-2';

  for (let i = 0; i < lines; i++) {
    const width = i === lines - 1 ? '70%' : '100%';
    content.appendChild(createTextSkeleton(width));
  }

  card.appendChild(content);

  // Actions area
  if (showActions) {
    const actions = document.createElement('div');
    actions.className = 'flex gap-2 mt-4 pt-4 border-t border-border/50';
    actions.appendChild(createSkeleton({ variant: 'rounded', width: 80, height: 32 }));
    actions.appendChild(createSkeleton({ variant: 'rounded', width: 80, height: 32 }));
    card.appendChild(actions);
  }

  return card;
}

/**
 * Create a summary card skeleton (for dashboard)
 */
export function createSummaryCardSkeleton(): HTMLElement {
  const card = document.createElement('div');
  card.className = 'glass-sm p-4 rounded-xl animate-pulse';
  card.setAttribute('role', 'status');
  card.setAttribute('aria-label', 'Loading summary...');

  card.innerHTML = `
    <div class="flex items-center gap-3">
      <div class="w-10 h-10 rounded-lg bg-muted"></div>
      <div class="flex-1">
        <div class="h-4 bg-muted rounded w-24 mb-2"></div>
        <div class="h-6 bg-muted rounded w-12"></div>
      </div>
    </div>
  `;

  return card;
}

/**
 * Create a table skeleton
 */
export function createTableSkeleton(options: TableSkeletonOptions = {}): HTMLElement {
  const {
    rows = 5,
    columns = 4,
    showHeader = true,
    className = '',
  } = options;

  const table = document.createElement('div');
  table.className = `skeleton-table w-full ${className}`.trim();
  table.setAttribute('role', 'status');
  table.setAttribute('aria-label', 'Loading table...');

  // Header row
  if (showHeader) {
    const header = document.createElement('div');
    header.className = 'flex gap-4 p-3 border-b border-border';

    for (let i = 0; i < columns; i++) {
      const headerCell = document.createElement('div');
      headerCell.className = 'flex-1';
      headerCell.appendChild(createSkeleton({ height: 16, width: '70%' }));
      header.appendChild(headerCell);
    }

    table.appendChild(header);
  }

  // Data rows
  for (let r = 0; r < rows; r++) {
    const row = document.createElement('div');
    row.className = 'flex gap-4 p-3 border-b border-border/50';

    for (let c = 0; c < columns; c++) {
      const cell = document.createElement('div');
      cell.className = 'flex-1';
      const width = c === 0 ? '80%' : c === columns - 1 ? '50%' : '60%';
      cell.appendChild(createTextSkeleton(width));
      row.appendChild(cell);
    }

    table.appendChild(row);
  }

  return table;
}

/**
 * Create a request detail skeleton
 */
export function createRequestDetailSkeleton(): HTMLElement {
  const container = document.createElement('div');
  container.className = 'space-y-6';
  container.setAttribute('role', 'status');
  container.setAttribute('aria-label', 'Loading request details...');

  // Header skeleton
  const header = document.createElement('div');
  header.className = 'flex items-start justify-between';
  header.innerHTML = `
    <div class="space-y-2">
      <div class="h-8 bg-muted rounded w-64 animate-pulse"></div>
      <div class="h-4 bg-muted rounded w-32 animate-pulse"></div>
    </div>
    <div class="h-8 bg-muted rounded-full w-24 animate-pulse"></div>
  `;
  container.appendChild(header);

  // Progress steps skeleton
  const progress = document.createElement('div');
  progress.className = 'flex items-center gap-4 py-4';
  for (let i = 0; i < 3; i++) {
    const step = document.createElement('div');
    step.className = 'flex items-center gap-2';
    step.innerHTML = `
      <div class="w-8 h-8 bg-muted rounded-full animate-pulse"></div>
      <div class="h-4 bg-muted rounded w-20 animate-pulse"></div>
    `;
    progress.appendChild(step);
    if (i < 2) {
      const connector = document.createElement('div');
      connector.className = 'h-0.5 bg-muted flex-1 animate-pulse';
      progress.appendChild(connector);
    }
  }
  container.appendChild(progress);

  // Details card skeleton
  const detailsCard = createCardSkeleton({
    showHeader: false,
    lines: 5,
    className: 'mt-4',
  });
  container.appendChild(detailsCard);

  // Comments skeleton
  const commentsCard = document.createElement('div');
  commentsCard.className = 'glass-card p-6 rounded-2xl mt-4';
  commentsCard.innerHTML = `
    <div class="h-6 bg-muted rounded w-32 mb-4 animate-pulse"></div>
  `;
  commentsCard.appendChild(createListSkeleton({ items: 2, showAvatar: true, lines: 2 }));
  container.appendChild(commentsCard);

  return container;
}

/**
 * Create a form skeleton
 */
export function createFormSkeleton(fields: number = 5): HTMLElement {
  const form = document.createElement('div');
  form.className = 'space-y-6';
  form.setAttribute('role', 'status');
  form.setAttribute('aria-label', 'Loading form...');

  for (let i = 0; i < fields; i++) {
    const field = document.createElement('div');
    field.className = 'space-y-2';

    // Label
    field.appendChild(createSkeleton({ width: '30%', height: 14 }));

    // Input
    field.appendChild(createSkeleton({ variant: 'rounded', width: '100%', height: 40 }));

    form.appendChild(field);
  }

  // Buttons
  const buttons = document.createElement('div');
  buttons.className = 'flex gap-3 pt-4';
  buttons.appendChild(createSkeleton({ variant: 'rounded', width: 100, height: 40 }));
  buttons.appendChild(createSkeleton({ variant: 'rounded', width: 120, height: 40 }));
  form.appendChild(buttons);

  return form;
}

/**
 * Skeleton class for programmatic control
 */
export class Skeleton {
  private element: HTMLElement;

  constructor(options: SkeletonOptions = {}) {
    this.element = createSkeleton(options);
  }

  /**
   * Get the skeleton element
   */
  getElement(): HTMLElement {
    return this.element;
  }

  /**
   * Show the skeleton in a container (replaces content)
   */
  show(container: HTMLElement): void {
    this.element.dataset.originalContent = container.innerHTML;
    container.innerHTML = '';
    container.appendChild(this.element);
  }

  /**
   * Hide the skeleton and restore original content
   */
  hide(): void {
    const parent = this.element.parentElement;
    if (parent && this.element.dataset.originalContent !== undefined) {
      parent.innerHTML = this.element.dataset.originalContent;
    } else {
      this.element.remove();
    }
  }
}

export default Skeleton;
